import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { FirmsService } from 'api/services/FirmsService';
import {
  FIRM_VALUATIONS_DEFINITION_CHART_DATA_DEFAULT_ERROR_MESSAGE,
  FIRM_VALUATIONS_DEFINITION_CHART_DATA_QUERY_KEY,
  FIRM_VALUATIONS_DEFINITION_CHART_DATA_STALE_TIME,
  SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
} from 'common/constants/services/firms';
import { throwError } from 'utilities';
import { ChartDataResponse, GetFirmValuationsChartDataReturn, UseGetFirmValuationsChartDataParams } from './types';

const getFirmValuationsChartData = async (
  firmId: number,
  definitionId: number,
  fundId?: number
): Promise<ChartDataResponse | null> => {
  try {
    const result = (await FirmsService.apiFirmsValuationsDefinitionsChartDataRead(
      String(firmId),
      String(definitionId),
      fundId
    )) as unknown as ChartDataResponse;

    if (!result) {
      return throwError(FIRM_VALUATIONS_DEFINITION_CHART_DATA_DEFAULT_ERROR_MESSAGE);
    }

    return result;
  } catch (error) {
    return throwError(FIRM_VALUATIONS_DEFINITION_CHART_DATA_DEFAULT_ERROR_MESSAGE, error);
  }
};

const useGetFirmValuationsChartData = (
  params: UseGetFirmValuationsChartDataParams
): GetFirmValuationsChartDataReturn => {
  const { firmId, definitionId, fundId, shouldQueryAutomatically = true } = params;

  const { data, error, isError, isFetched, isFetching, isLoading, refetch } = useQuery<
    GetFirmValuationsChartDataReturn['data'],
    Error
  >({
    enabled: !isNil(firmId) && !isNil(definitionId) && shouldQueryAutomatically,
    queryFn: () => getFirmValuationsChartData(firmId!, definitionId!, fundId),
    queryKey: [FIRM_VALUATIONS_DEFINITION_CHART_DATA_QUERY_KEY, firmId, definitionId, fundId],
    refetchOnWindowFocus: SERVICES_FIRMS_REFETCH_ON_WINDOW_FOCUS,
    staleTime: FIRM_VALUATIONS_DEFINITION_CHART_DATA_STALE_TIME,
  });

  return {
    data,
    error,
    isError,
    isFetched,
    isFetching,
    isLoading,
    refetch,
  };
};

export default useGetFirmValuationsChartData;
